import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import { apiService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

/**
 * Le CUL-DE-SAC « aucun stock sélectionné », et sa sortie pour un ADMIN.
 *
 * ── Pourquoi un COMPOSANT plutôt que du JSX en ligne dans App.js ──────────
 * L'écran a besoin de `t()` et d'un état de dialogue. Les mettre dans
 * `AppContent` aurait abonné CE composant-là à LanguageContext — or le
 * `value` du provider est un objet littéral RECRÉÉ À CHAQUE RENDU (il n'est
 * pas mémoïsé, cf. LanguageContext.js:80). `AppContent` n'est aujourd'hui PAS
 * consommateur : il ne re-rend donc pas au chargement des traductions ni au
 * changement de langue, et il est la racine du châssis métier (Header,
 * Sidebar, Routes). L'y abonner aurait imposé ce coût au chemin de TOUS les
 * utilisateurs pour un écran que presque aucun ne voit.
 * Ici l'abonnement est porté par un composant qui n'est monté QUE dans le
 * cul-de-sac. Le coût est nul ailleurs.
 *
 * ── ⚠️ showSnackbar est INERTE depuis cet écran ───────────────────────────
 * Le <Snackbar> d'App.js n'est rendu que dans son `return` final, après les
 * sorties anticipées : un appel à `showSnackbar` depuis ici changerait un
 * state que RIEN n'affiche. La prop n'est volontairement pas reçue, et les
 * erreurs sont rendues LOCALEMENT dans le dialogue. Un appel sans effet est
 * pire qu'une absence : il fait croire au lecteur que l'erreur est traitée.
 */
function NoStockGate({ user, onStockCreated, banner, spacer }) {
  const { t } = useLanguage();

  const estAdmin = user?.role === 'ADMIN';

  const [ouvert, setOuvert] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  // ⚠️ CINQ CHAMPS, ET CE N'EST PAS DU CONFORT. Le stock porte les coordonnées
  // qui figurent sur le BON DE COMMANDE envoyé au fournisseur : `Stock.name` et
  // `Stock.address` sont déjà lues par `pdf_service` et `email_service`.
  // Un stock créé avec le seul nom produit donc un bon de commande INCOMPLET,
  // et l'ADMIN ne le découvre qu'à sa première commande — chez son fournisseur.
  // Le tunnel du lot 6f collectera les mêmes informations : ce bouton est un
  // SECOND chemin de création, il doit collecter la même chose.
  const [form, setForm] = useState({
    name: '', address: '', phone: '', email: '', description: '',
  });
  const maj = (champ) => (ev) => setForm((f) => ({ ...f, [champ]: ev.target.value }));

  // Obligatoires CÔTÉ DIALOGUE seulement — la route les accepte vides. Le coût
  // de les manquer est un bon de commande inexploitable découvert par le
  // fournisseur, pas une erreur rattrapable. L'email reste facultatif : la
  // commande part déjà par mail depuis le compte de l'expéditeur.
  const complet = form.name.trim() && form.address.trim() && form.phone.trim();

  const fermer = () => {
    if (envoi) return;
    setOuvert(false);
    setErreur('');
  };

  const soumettre = async (ev) => {
    ev.preventDefault();
    setErreur('');
    setEnvoi(true);
    try {
      const { data } = await apiService.post('/admin/stocks', {
        name: form.name.trim(),
        address: form.address.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        description: form.description.trim(),
      });
      // Le parent pose le stock courant ET rafraîchit la liste `stocks` : sans
      // la seconde, le sélecteur de la Sidebar resterait vide (Sidebar.js:183).
      onStockCreated(data.stock);
    } catch (e) {
      // ⚠️ Le serveur porte plusieurs refus distincts sur ce chemin — nom déjà
      // pris DANS L'ENTREPRISE (400) et cap de stocks du plan atteint (409) —
      // et leurs messages sont exploitables tels quels. On les affiche donc
      // plutôt qu'un libellé générique qui masquerait la cause.
      setErreur(e?.response?.data?.error || t('noStock.createError'));
      setEnvoi(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          alignItems: 'center', minHeight: '100vh', p: 2, pb: spacer,
        }}
      >
        {/* ⚠️ SÉLECTEUR DE LANGUE, même forme que sur la page d'invitation, et
            pour la même raison : il ne vit ailleurs que dans Header et
            SuperAdminShell — donc À L'INTÉRIEUR de l'application. Or on est ici
            précisément parce qu'on n'y est pas entré. Sans lui, la langue vaut
            le défaut 'fr' câblé dans LanguageContext, sans aucun recours.
            Monté pour les DEUX publics : le USER sans stock en a autant besoin
            que l'ADMIN — il est même celui qui risque d'y rester le plus
            longtemps, puisque sa sortie dépend de quelqu'un d'autre. */}
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <LanguageSwitcher />
        </Box>

        <Stack spacing={2} alignItems="center" sx={{ maxWidth: 420, textAlign: 'center' }}>
          {estAdmin ? (
            <>
              <Typography variant="h6">{t('noStock.adminTitle')}</Typography>
              <Typography color="text.secondary">{t('noStock.adminHelp')}</Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddBusinessIcon />}
                onClick={() => setOuvert(true)}
              >
                {t('noStock.createFirst')}
              </Button>
            </>
          ) : (
            /* Un USER sans stock est un cas LÉGITIME : son administrateur ne lui
               en a pas encore attribué. Il n'a rien à créer — le message actuel
               est le bon, et lui montrer un bouton qu'il ne peut pas utiliser
               serait une fausse piste. */
            <Typography variant="h6" color="text.secondary">
              {t('noStock.userMessage')}
            </Typography>
          )}
        </Stack>
      </Box>

      <Dialog open={ouvert} onClose={fermer} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={soumettre}>
          <DialogTitle>{t('noStock.dialogTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {t('noStock.dialogHelp')}
            </DialogContentText>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label={t('noStock.nameLabel')} value={form.name}
                onChange={maj('name')} required autoFocus fullWidth disabled={envoi}
              />
              {/* Le texte d'aide DIT OÙ VA LA DONNÉE. Sans lui, l'adresse et le
                  téléphone passent pour de l'administratif facultatif, alors
                  qu'ils sont ce que le fournisseur lira. */}
              <TextField
                label={t('noStock.addressLabel')} value={form.address}
                onChange={maj('address')} required fullWidth multiline rows={2}
                disabled={envoi} helperText={t('noStock.onPurchaseOrders')}
              />
              <TextField
                label={t('noStock.phoneLabel')} value={form.phone}
                onChange={maj('phone')} required fullWidth disabled={envoi}
                helperText={t('noStock.onPurchaseOrders')}
              />
              <TextField
                label={t('noStock.emailLabel')} type="email" value={form.email}
                onChange={maj('email')} fullWidth disabled={envoi}
                helperText={t('noStock.emailHelp')}
              />
              <TextField
                label={t('noStock.descriptionLabel')} value={form.description}
                onChange={maj('description')} fullWidth disabled={envoi}
                helperText={t('noStock.descriptionHelp')}
              />
            </Stack>
            {erreur && <Alert severity="error" sx={{ mt: 2 }}>{erreur}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={fermer} disabled={envoi}>{t('common.cancel')}</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={envoi || !complet}
              startIcon={envoi ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {envoi ? t('noStock.creating') : t('noStock.create')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {banner}
    </>
  );
}

export default NoStockGate;
